import type { ISO8601DateTimeString, ProjectId, UserId, UUIDv4 } from "../types";
import type { Category } from "./categories";
import type { CompletionTrackingType } from "./completionTracking";

// Plain string-literal union — enumerates every key in statusColors (buildStatusColors ∪ bim360StatusColors).
// When the UI maps are retyped as Record<StatusColor, string>, TypeScript will catch any key drift here.
export type StatusColor =
  // buildStatusColors keys (from apps/acs-assets/src/models/statusSets.ts:17-61)
  | "adsk-charcoal-900"
  | "adsk-charcoal-800"
  | "adsk-charcoal-700"
  | "adsk-charcoal-600"
  | "adsk-charcoal-500"
  | "adsk-charcoal-400"
  | "adsk-charcoal-300"
  | "adsk-charcoal-200"
  | "adsk-charcoal-100"
  | "adsk-charcoal-050"
  | "adsk-red-700"
  | "adsk-red-500"
  | "adsk-red-300"
  | "adsk-salmon-300"
  | "adsk-green-700"
  | "adsk-green-500"
  | "adsk-green-300"
  | "adsk-green-100"
  | "adsk-yellow-orange-700"
  | "adsk-yellow-orange-500"
  | "adsk-yellow-orange-300"
  | "adsk-blue-700"
  | "adsk-blue-500"
  | "adsk-blue-100"
  | "adsk-dark-blue-700"
  | "adsk-dark-blue-500"
  | "adsk-dark-blue-300"
  | "adsk-pink-700"
  | "adsk-pink-500"
  | "adsk-pink-300"
  | "adsk-turquoise-700"
  | "adsk-turquoise-500"
  | "adsk-turquoise-300"
  | "adsk-purple-700"
  | "adsk-purple-500"
  | "adsk-purple-300"
  | "adsk-salmon-700"
  | "adsk-salmon-500"
  | "adsk-brown-700"
  | "adsk-brown-500"
  | "adsk-brown-300"
  | "yellow-green-alpha-10" // UNSPECIFIED_STATUS_COLOR_KEY sentinel
  | "adsk-white"
  // bim360StatusColors keys (from apps/acs-assets/src/models/statusSets.ts:63-76)
  | "red-orange"
  | "red"
  | "magenta"
  | "indigo"
  | "purple"
  | "dark-blue"
  | "blue"
  | "light-blue"
  | "turquoise"
  | "sea-green"
  | "green"
  | "light-green"
  | "yellow-green"
  | "yellow"
  | "yellow-orange"
  | "orange"
  | "brown"
  | "pink";

export type StatusSetId = StatusSet["id"];
export type StatusId = Status["id"];

// Canonical Status shape — shared-web's stricter typing wins (isActive/projectId required, sortOrder required)
// Source: libs/acs-assets-shared-web/src/models/statusSets.ts:85-106
export type Status = {
  description?: string;
  label: string;
  color: Exclude<StatusColor, "yellow-green-alpha-10">;
  completionTracking?: CompletionTrackingType | null;
  statusStepSetId: StatusSetId;
  id: UUIDv4;
  createdAt: Date;
  createdBy: UserId;
  updatedAt: Date;
  updatedBy: UserId;
  deletedAt?: Date;
  deletedBy?: UserId;
  isActive: boolean; // required (shared-web stricter wins)
  version: number;
  bucket: string;
  projectId: ProjectId; // required (shared-web stricter wins)
  sortOrder: number; // required (real DB field — asset_status.sort_order NOT NULL)
};

// Source: libs/acs-assets-shared-web/src/models/statusSets.ts:108-124
export type StatusSet = {
  name: string;
  description?: string;
  values: Status[];
  id: UUIDv4;
  createdAt: Date;
  createdBy: UserId;
  updatedAt: Date;
  updatedBy: UserId;
  deletedAt?: Date;
  deletedBy?: UserId;
  isActive?: boolean;
  version: number;
  projectId?: ProjectId;
  isDefault?: boolean;
  inheritedFromCategoryId?: Category["id"]; // bidirectional cycle (type-only, safe)
};

export type StatusDTO = Pick<
  Status,
  Exclude<keyof Status, "createdAt" | "updatedAt" | "deletedAt">
> & {
  createdAt: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
  deletedAt?: ISO8601DateTimeString;
};

export type StatusSetDTO = Pick<
  StatusSet,
  Exclude<keyof StatusSet, "values" | "createdAt" | "updatedAt" | "deletedAt">
> & {
  values: StatusDTO[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
};

// Ordering endpoint types (PUT .../status-step-sets/{id}/order)
// StatusOrderResponse is the return type of statusSetClient.ts's `setOrder`, which
// apps/acs-assets/src/clients/getStatusSetsAPIClient.ts delegates to directly (issue #2500).
export type AssetStatusIdList = {
  assetStatusIds: StatusId[];
};

export type StatusOrderResponse = {
  id: StatusSetId;
  values: Array<{ id: StatusId; sortOrder: number }>;
};

// Status creation / update machinery — pure, no UI dep
// Source: apps/acs-assets/src/models/statusSets.ts:191-281
export type NewStatus = {
  statusStepSetId: StatusSetId;
  label: string;
  color: Exclude<StatusColor, "yellow-green-alpha-10">;
  description?: string;
  completionTracking?: CompletionTrackingType | null;
};

export type NewStatusSetStatus = Omit<NewStatus, "statusStepSetId"> & {
  sortOrder?: number;
};

export type NewStatusSet = {
  name: string;
  description?: string;
  values?: NewStatusSetStatus[];
};

export type TempNewStatusSet = Omit<NewStatusSet, "values"> & {
  values: (NewStatusSetStatus & { id: UUIDv4 })[];
};

type MutableStatusProperty = Extract<
  keyof Status,
  "label" | "description" | "color" | "completionTracking"
>;

export type StatusError = Partial<Record<MutableStatusProperty, string>>;
export type StatusTouched = Partial<Record<MutableStatusProperty, boolean>>;
export type UpdateStatusSetObject = Partial<
  Pick<StatusSet, "name" | "description">
>;
export type UpdateStatusObject = Partial<
  Pick<Status, "label" | "color" | "description" | "completionTracking">
>;

// Parsers
export const parseStatus = ({ deletedAt, ...status }: StatusDTO): Status => ({
  ...status,
  createdAt: new Date(status.createdAt),
  updatedAt: new Date(status.updatedAt),
  ...(deletedAt && { deletedAt: new Date(deletedAt) }),
});

// Pure version — no i18n. App wraps this to rename default status sets.
// Source: libs/acs-assets-shared-web/src/models/statusSets.ts:169-178
export const parseStatusSet = ({
  deletedAt,
  ...statusSet
}: StatusSetDTO): StatusSet => ({
  ...statusSet,
  values: statusSet.values.map(parseStatus),
  createdAt: new Date(statusSet.createdAt),
  updatedAt: new Date(statusSet.updatedAt),
  ...(deletedAt && { deletedAt: new Date(deletedAt) }),
});

export const parseStatusSetList = (statusSets: StatusSetDTO[]): StatusSet[] =>
  statusSets.map(parseStatusSet);

// Serializers — source: apps/acs-assets/src/models/statusSets.ts:262-281
export function serializeNewStatus<T extends NewStatus | NewStatusSetStatus>({
  description,
  ...status
}: T): T {
  return {
    ...status,
    ...(typeof description === "string" &&
      description.trim().length > 0 && { description }),
  } as T;
}

export const serializeNewStatusSet = ({
  description,
  ...statusSet
}: NewStatusSet): NewStatusSet => ({
  ...statusSet,
  ...(typeof description === "string" &&
    description.trim().length > 0 && { description }),
  values: statusSet.values && statusSet.values.map(serializeNewStatus),
});
